import clsx from 'clsx';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import React from 'react';
import {Box} from '@mui/material';
import {Body, Tooltip} from '@lightricks/react-design-system';
import translate from '@/utils/translate';
import {MONTH_SHORT_DAY_YEAR_FORMAT} from '@/config/timeFormats';
import type {CampaignCreatorRow} from '@/views/campaigns/creators-tab/types/creatorTabTypes';
import Tag from '@/components/tag/Tag';
import styles from './AssetsCell.module.scss';
import AssetsProgressMeter from './AssetsProgressMeter';
import {deriveAssetsStatus, describeDeliverable} from './assetsCellStatus';

dayjs.extend(utc);

interface AssetsCellProps {
  creator: CampaignCreatorRow;
}

function DrillDown({creator}: AssetsCellProps) {
  const {deliverables, contentDue} = creator;
  const formattedDue = contentDue
    ? dayjs.utc(contentDue).format(MONTH_SHORT_DAY_YEAR_FORMAT)
    : null;

  // Synthesized slots have no id; index suffix keeps keys unique in this static list.
  const rows = deliverables.map((deliverable, index) => ({
    deliverable,
    key: `${deliverable.kind}-${deliverable.state}-${index}`,
  }));

  return (
    <div className={styles.drillDown}>
      {formattedDue ? (
        <Body size="sm" className={styles.drillDownDue}>
          {translate('Due {date}', {date: formattedDue})}
        </Body>
      ) : null}
      {rows.length > 0 ? (
        rows.map(({deliverable, key}) => (
          <Body key={key} size="sm">
            {describeDeliverable(deliverable)}
          </Body>
        ))
      ) : (
        <Body size="sm">{translate('No deliverables yet')}</Body>
      )}
    </div>
  );
}

function AssetsCell({creator}: AssetsCellProps) {
  const {
    acceptedAssetsCount,
    requiredAssetsCount,
    pendingAssetsCount,
    editsRequestedAssetsCount,
    notSubmittedCount,
    extraAssetsCount,
    isOverdue,
    dueSoon,
  } = creator;

  if (acceptedAssetsCount == null || requiredAssetsCount == null) {
    return <Body size="md">-</Body>;
  }

  const status = deriveAssetsStatus(creator);

  // Counts are capped per kind by the serializer, so requiredAssetsCount is the
  // meaningful denominator. A gig with no requirement has nothing to chart —
  // surface the status on its own rather than a "0/0" meter.
  if (requiredAssetsCount === 0) {
    return status ? (
      <Box component="span" className={styles.cell}>
        <Tag label={status.label} color={status.color} size="small" />
      </Box>
    ) : (
      <Body size="md">-</Body>
    );
  }

  const extraCount = extraAssetsCount ?? 0;

  return (
    <Tooltip
      title={<DrillDown creator={creator} />}
      arrow
      placement="top"
      className={styles.tooltip}
    >
      <Box component="span" className={styles.cell}>
        {status ? (
          <Tag label={status.label} color={status.color} size="small" />
        ) : null}
        <div className={styles.meterRow}>
          <AssetsProgressMeter
            acceptedCount={acceptedAssetsCount}
            pendingCount={pendingAssetsCount ?? 0}
            editsRequestedCount={editsRequestedAssetsCount ?? 0}
            notSubmittedCount={notSubmittedCount ?? 0}
          />
          <Body
            size="sm"
            className={clsx(styles.count, {
              [styles.countOverdue]: isOverdue,
              [styles.countDueSoon]: !isOverdue && dueSoon,
            })}
          >
            {`${acceptedAssetsCount}/${requiredAssetsCount}`}
          </Body>
          {extraCount > 0 ? (
            <Body size="sm" className={styles.extra}>
              {translate('+{count}', {count: extraCount})}
            </Body>
          ) : null}
        </div>
      </Box>
    </Tooltip>
  );
}

export default React.memo(AssetsCell);
